import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import createWidget from 'siempreplus-widget';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { readClientApiKey } from 'utils/clientApiKey';
import { CurrentLang } from 'utils/lang';
import { selectLoginOnCheckoutEnabled } from 'selectors/gontijoSessionSelectors';
import errorMonitoring from 'errorMonitoring';

/**
 * The header's breakpoint (`$screen-medium` / `$screen-large` in `_breakpoints.scss`). Both bars are
 * always in the DOM and CSS hides one, so each header mounts its widget only on its own side of it.
 */
export const GONTIJO_MOBILE_HEADER_MEDIA = '(max-width: 1080px)';
export const GONTIJO_DESKTOP_HEADER_MEDIA = '(min-width: 1081px)';

/**
 * Whether `media` matches now, following it as the viewport changes. No query, or no `matchMedia`
 * (jsdom), always matches.
 * @param {string} [media] - A media query.
 * @returns {boolean} Whether the query matches.
 */
function useMediaMatches(media) {
  const [matches, setMatches] = useState(
    () => !media || !window.matchMedia || window.matchMedia(media).matches,
  );

  useEffect(() => {
    if (!media || !window.matchMedia) return undefined;

    const mql = window.matchMedia(media);
    const onChange = (e) => setMatches(e.matches);

    // `addListener` first, as `useIsMobile` does, for the Safari versions without the other.
    if (mql.addListener) mql.addListener(onChange);
    else mql.addEventListener('change', onChange);
    setMatches(mql.matches);

    return () => {
      if (mql.removeListener) mql.removeListener(onChange);
      else mql.removeEventListener('change', onChange);
    };
  }, [media]);

  return matches;
}

/**
 * The Gontijo account entry point, drawn by the shared sign-in widget.
 *
 * Deliberately not routed through `LoyaltyButton`/`LoyaltyWidget`: those are built around
 * `env.siemprePlus` and gate on `DOTERS_ENABLED`/`COSTAPASS_ENABLED`, and bending them to a third
 * program would put Gontijo's props on the path ETN and CostaPass render through. Mounting the
 * widget here costs a few attributes and leaves that path untouched.
 *
 * The widget owns the form and the code step; it does not own the session. It announces the minted
 * token on its own channel, and the session handler is what turns that into a session the funnel
 * can spend.
 *
 * The same package `LoyaltyWidget` mounts for ETN, Doters and CostaPass: as of 6.0.0 the login, the
 * code step, registration and the app's look all switch on for `card-type="gontijo"` alone, so
 * there is no separate build to fetch. A brand without the flag never mounts this one.
 *
 * Every instance is its own preact root, and on mount each one validates the cookie session with
 * its own `validate_token`: the widget does not share a restored session between roots. Hence
 * `media`, so the two headers mount one instance between them, and `startupOnMount`, for a host
 * that has no session to restore. A breakpoint change mounts the other header's instance and
 * leaves the old root orphaned in memory; the widget exposes no unmount.
 *
 * A failed mount leaves the host without a trigger, so it is reported and handed to `onMountError`.
 * A new `mountKey` is the retry: it swaps in a fresh host node and mounts into it again. How many
 * retries, and when, is the caller's call; the headers pass none and keep their one attempt. Only
 * the latest attempt answers the callbacks, so a slow earlier one cannot overrule it.
 * @param {object} props - Component props.
 * @param {string} props.id - Unique mount id, so two headers can each render one.
 * @param {string} [props.media] - Mount only while this media query matches.
 * @param {boolean} [props.startupOnMount] - Whether the widget restores the cookie session on mount.
 * @param {number} [props.mountKey] - Bump to remount the widget into a fresh host.
 * @param {() => void} [props.onMount] - Called once an attempt has mounted.
 * @param {(error: Error) => void} [props.onMountError] - Called when an attempt fails to mount.
 * @returns {React.ReactElement|null} The widget host, or nothing for a brand without the flag.
 */
function GontijoLoginWidget({
  id,
  media,
  startupOnMount = true,
  mountKey = 0,
  onMount,
  onMountError,
}) {
  const enabled = useSelector(selectLoginOnCheckoutEnabled);
  const { env } = useSelector((state) => state.whitelabelConfig);
  const { t } = useTranslation('payment');
  const visible = useMediaMatches(media);
  const shown = enabled && visible;

  // Read at settle time, so a new callback identity does not remount the widget.
  const callbacksRef = useRef({ onMount, onMountError });
  callbacksRef.current = { onMount, onMountError };

  // Once per host: a host that comes back after a breakpoint change, or a new `mountKey`, is a
  // fresh node to mount into.
  useEffect(() => {
    if (!shown) return undefined;

    let latest = true;
    createWidget(`#${id}`).then(
      () => {
        if (latest) callbacksRef.current.onMount?.();
      },
      (error) => {
        errorMonitoring.notify({
          error,
          info: { from: 'GontijoLoginWidget.createWidget', id, mountKey },
        });
        if (latest) callbacksRef.current.onMountError?.(error);
      },
    );

    return () => {
      latest = false;
    };
  }, [shown, id, mountKey]);

  if (!shown) return null;

  return (
    <div
      // A new node per attempt: a failed mount may have left a half-drawn root behind.
      key={mountKey}
      id={id}
      // eslint-disable-next-line react/no-unknown-property
      widget="login"
      data-prop-card-type="gontijo"
      // Gontijo's auth routes live on platform-purchase, not on the loyalty host every other card
      // type points at. `endpoint-name` is what puts them under `/api/v2/gontijo/auth`.
      data-prop-origin={env.api.purchaseUrl?.replace(/\/api\/?$/, '')}
      data-prop-endpoint-name="gontijo/auth"
      // The greeting's name, and nothing else, rides on this. `validate_token` answers who the
      // session belongs to only to a caller that identifies itself with the tenant's key —
      // `client_payload` in platform-purchase's AuthController gates exactly that block — so
      // without it the widget signs the buyer in and then calls them "Mi cuenta".
      //
      // The key boot resolved for this platform — the one every purchase call from this page
      // already carries — so the widget does not identify as mobile web from a desktop header.
      // The widget sends it as an `Authorization` header, never in the URL. It is a browser-side
      // client key that ships in this bundle either way; the attribute publishes nothing new.
      data-prop-api-key={readClientApiKey() ?? undefined}
      // `language`, not `lang`: the widget reads the first and ignores the second, so this was
      // travelling and landing nowhere — a Brazilian buyer got the Spanish copy. `CurrentLang`
      // already answers `pt-BR` here, which the widget speaks as of 6.0.0.
      data-prop-language={CurrentLang()}
      data-prop-display-type="dropdown"
      data-prop-variant="link"
      data-prop-modifier-class="header-app"
      data-prop-sign-in-title={t('gontijo_auth.modal_title')}
      data-prop-button-text={t('gontijo_auth.sign_in')}
      data-prop-primary-color={env.theme?.colors?.primary}
      data-prop-accent-color={env.theme?.colors?.accent}
      // Recovery is still nobody's, so the link stays off rather than leading nowhere (BOOK-74).
      data-prop-recover-password="false"
      // Registration is the widget's now: the same fourteen fields the app asks for, with the
      // address check in front of them. Needs 6.0.0 or newer; against an older build the attribute
      // turns on a screen that does not exist yet, so the two travel together.
      data-prop-register-account="true"
      data-prop-third-party="false"
      data-prop-startup-on-mount={startupOnMount ? undefined : 'false'}
    />
  );
}

GontijoLoginWidget.propTypes = {
  id: PropTypes.string.isRequired,
  media: PropTypes.string,
  startupOnMount: PropTypes.bool,
  mountKey: PropTypes.number,
  onMount: PropTypes.func,
  onMountError: PropTypes.func,
};

export default GontijoLoginWidget;
